import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Table, Tag, Button, Modal, notification, Tooltip } from 'antd';
import { EyeOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { getIssues, approveIssues } from '../../../../../helpers/apiCalls';
import useTemplateState from '../../../../../../Template/atoms/variables';
import useGlobalState from '../../../../../../Template/atoms/variables';
import IssueDetailsModal from '../CommissioningTab/CriteriaTypes/WebFormOpenner/IssueDetailsModal';

const IssuesTab = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { assetId, interconnectionId } = useParams();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState([]);

  const {
    setModalContent,
    setModalTitle,
    setModalStyle,
    setModalConfirm,
    setCleanOnCancel,
    setModalOpen,
    setPendingApiCalls,
  } = useTemplateState();

  const { user } = useGlobalState();

  useEffect(() => {
    fetchAllIssues();
  }, [assetId, interconnectionId]);

  const fetchAllIssues = async () => {
    setLoading(true);
    setPendingApiCalls((current) => current.concat(['getIssues']));
    
    try {
      const response = await getIssues(instance, {
        'subject._id': assetId ?? interconnectionId,
        'subject._type': assetId ? 'Asset' : 'Interconnection',
        uniqueValues: false,
        limit: 100,
      });

      setIssues(response?.items || []);
    } catch (error) {
      console.error('Error fetching issues:', error);
      notificationApi.error({
        message: t('database_communication_failure'),
        description: error?.response?.data?.message || error?.message,
        duration: 5,
      });
    } finally {
      setLoading(false);
      setPendingApiCalls((current) => current.filter((item) => item !== 'getIssues'));
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'green';
      case 'pending':
        return 'orange';
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  };

  const handleViewIssue = (issue) => {
    setModalContent(
      <IssueDetailsModal
        stage={issue.stage}
        givenIssue={issue}
        handleInsertComment={() => {}}
        setForceReload={() => {
          fetchAllIssues();
        }}
      />
    );
    setModalTitle(`${t('issue')} - ${issue.title || issue._id}`);
    setModalStyle({
      padding: '2rem',
      gap: '1rem',
      maxHeight: '100%',
    });
    setModalConfirm(null);
    setCleanOnCancel(true);
    setModalOpen(true);
  };

  const createNewIssue = () => {
    setModalContent(
      <IssueDetailsModal
        stage={null}
        givenIssue={null}
        handleInsertComment={() => {}}
        setForceReload={() => {
          fetchAllIssues();
        }}
      />
    );
    setModalTitle(t('create_issue'));
    setModalStyle({
      padding: '2rem',
      gap: '1rem',
      maxHeight: '100%',
    });
    setModalConfirm(null);
    setCleanOnCancel(true);
    setModalOpen(true);
  };

  const handleApproveIssues = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['approveIssues']));
      let ids = selectedIssues.map((t) => t._id);
      const approved_by = user?._id;
      
      await approveIssues(instance, ids, approved_by);

      setIssues((current) =>
        current.map((issue) =>
          ids.some((id) => id === issue._id) ? { ...issue, status: 'Approved' } : issue,
        ),
      );
      setSelectedIssues([]);
      fetchAllIssues();
    } catch (error) {
      console.error('Error approving issues:', error);
      notificationApi.error({
        message: t('error_approving_issues') || 'Error approving issues',
        description: error?.response?.data?.message || error?.message,
        duration: 5,
      });
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'approveIssues'));
    }
  };

  const getApproveTooltip = () => {
    if (!selectedIssues.length) return t('select_issues_to_approve') || 'Select issues to approve';
    const pendingIssues = selectedIssues.filter(issue => issue.status?.toLowerCase() === 'pending');
    if (pendingIssues.length === 0) return t('no_pending_issues_selected') || 'No pending issues selected';
    return `${t('approve')} ${pendingIssues.length} ${t('pending_issues')}` || `Approve ${pendingIssues.length} pending issues`;
  };

  const columns = [
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => text || record._id,
    },
    {
      title: t('stage'),
      dataIndex: 'stage',
      key: 'stage',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
    {
      title: t('created_at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
      },
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewIssue(record)}
        >
          {t('view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="issues-tab" style={{ padding: '1rem' }}>
      {contextHolder}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1rem' 
      }}>
        <h2>{t('issues')}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title={getApproveTooltip()}>
            <Button
              disabled={selectedIssues.filter(issue => issue.status?.toLowerCase() === 'pending').length === 0}
              size='large'
              style={{
                borderColor: '#52C41A',
                background: '#52C41A',
                color: 'white',
                opacity: selectedIssues.filter(issue => issue.status?.toLowerCase() === 'pending').length !== 0 ? 1 : 0.5,
              }}
              icon={<CheckCircleOutlined />}
              onClick={handleApproveIssues}
            >
              {t('approve')}
            </Button>
          </Tooltip>
          <Button
            type="primary"
            style={{
              backgroundColor: '#1777FF',
              borderColor: '#1777FF',
            }}
            onClick={createNewIssue}
          >
            {t('create_issue')}
          </Button>
        </div>
      </div>
      
      <Table
        columns={columns}
        dataSource={issues}
        rowKey="_id"
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectedIssues.map(item => item._id),
          onChange: (_, selectedRows) => {
            setSelectedIssues(selectedRows);
          },
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
        }}
      />
    </div>
  );
};

export default IssuesTab;